import React, {RefObject, useEffect, useRef, useState} from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import {useReducedMotion} from 'react-native-reanimated';
import {observer} from 'mobx-react-lite';
import {useDebounce} from 'use-debounce';

import {control, radius, space, universal} from '../../design-system/tokens';
import {MIN_SEARCH_KEYWORDS} from '../../queries/search/useGlobalSearchLines';
import {OrderLine} from '../../transport/apiServiceResponse';
import {filterSearchText} from '../../utils/string';
import ProductTileHorizontal from '../ProductTile/variants/ProductTileHorizontal';
import {SearchItemSkeleton} from '../skeleton';

import ChevronRightImg from './assets/ChevronRight.svg';
import SearchClearImg from './assets/SearchClear.svg';
import SearchIconImg from './assets/SearchIcon.svg';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {hiddenWhen, useProgress} from './motion';
import {HEADER_SEARCH_LIMIT, useHeaderSearchLines} from './useHeaderSearchLines';

export {HEADER_SEARCH_LIMIT};

export const HEADER_SEARCH_PLACEHOLDER = 'Search beers, wines, spirits & more';

// SearchScreen's debounce and input cap (SearchScreen.tsx useDebounce, SearchBar maxLength).
const DEBOUNCE_MS = 300;
const MAX_LENGTH = 80;
// SHORTCUT: icon sizes are Figma literals with no icon-size token: magnifier 14 (196:26065),
// chevron 16 (196:25418), clear glyph 16 in a 24 hit frame (230:7122). Upgrade path: a DIS-1735
// icon-size scale. Each SVG call site repeats the file's own viewBox, which the web svgr config
// strips.
const SEARCH_ICON_SIZE = 14;
const CHEVRON_SIZE = 16;
const CLEAR_ICON_SIZE = 16;
const CLEAR_HIT_SIZE = 24;
// The trailing slot keeps the magnifier's frame; the clear hit frame overhangs it this much on
// every side, centred on the magnifier.
const CLEAR_OVERHANG = (CLEAR_HIT_SIZE - SEARCH_ICON_SIZE) / 2;
// SHORTCUT: the scale a glyph shrinks to as it fades out of the trailing slot (DIS-2012); a
// prototype choice, no motion token. Upgrade path: a DIS-1735 motion token or design's value.
const GLYPH_HIDDEN_SCALE = 0.6;
// SHORTCUT: the 24 header row is a Figma layout literal (196:25414); no token. Upgrade path:
// DIS-1735 layout tokens.
const HEADER_ROW_HEIGHT = 24;
// SHORTCUT: no shadow token; the mobile header's compact shadow (MobileHeader SHADOW). Figma
// 196:25412 draws only a top border, so over a white page the panel has no edge. Upgrade path:
// design confirms or supplies a DIS-1735 elevation token.
const PANEL_SHADOW = '0px 4px 2px rgba(0, 0, 0, 0.1)';
// Chrome's :focus-visible outline is rgb(0, 95, 204) auto around the text run, inside the
// pill (Headless Chrome 156, keyboard focus; the icon sits outside that rectangle). Width 0
// does not stop the auto ring, so outlineStyle 'none' is the load-bearing key, as FormField's
// WEB_INPUT_NO_OUTLINE. react-native-web accepts 'none'; RN's TextStyle does not.
const WEB_INPUT_NO_OUTLINE = {outlineWidth: 0, outlineStyle: 'none'} as unknown as TextStyle;

/** On web a host ref is a DOM element; elsewhere it has no `contains`. */
const containsTarget = (node: unknown, target: unknown): boolean =>
  typeof (node as {contains?: unknown} | null)?.contains === 'function' &&
  (node as {contains(t: unknown): boolean}).contains(target);

export type HeaderSearchProps = {
  /** The field box; the input is `${testID}-input` and the panel `${testID}-panel`. */
  testID: string;
  /** The field box: the header owns its size and chrome per band. */
  style: StyleProp<ViewStyle>;
  /** The input text: the header owns its type per band. */
  textStyle: StyleProp<TextStyle>;
  /** The panel's height cap; its rows scroll inside. The header gives the window below itself. */
  panelMaxHeight: number;
  /** Enter or "View All Results": BrandHeader opens SearchScreen with the keywords. */
  onSubmit(keywords: string): void;
  /** A suggestion card was pressed: BrandHeader opens the product. */
  onResultPress(line: OrderLine): void;
  /** The input took focus (DesktopHeader closes an open mega menu). */
  onFocus?(): void;
  /**
   * Escape typed in the input, panel open or not. RNW's TextInput stops keydown propagation, so
   * DesktopHeader's document listener never sees it; DesktopHeader closes an open mega menu here.
   */
  onEscape?(): void;
  /** The panel opened or closed (or unmounted open): the header shows its backdrop meanwhile. */
  onOpenChange?(open: boolean): void;
  /**
   * The header's backdrop under the panel (QA 2026-10-21). A pointerdown on it is not outside,
   * so the panel and backdrop stay up and the backdrop takes the click; the click closes.
   */
  backdropRef?: RefObject<View | null>;
};

/**
 * Web header search (DIS-1907): a type-ahead field. From MIN_SEARCH_KEYWORDS characters a panel
 * under the field shows the top HEADER_SEARCH_LIMIT SearchScreen results as SearchScreen's cards
 * (ProductTileHorizontal). Enter and "View All Results" call `onSubmit`; Escape and a press
 * outside the field and panel close the panel (web). Figma: Desktop 196:25412, Mobile 475:23215.
 */
export const HeaderSearch = observer(function HeaderSearch({
  testID,
  style,
  textStyle,
  panelMaxHeight,
  onSubmit,
  onResultPress,
  onFocus,
  onEscape,
  onOpenChange,
  backdropRef,
}: HeaderSearchProps) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  // Escape or an outside press; the next edit or focus opens the panel again.
  const [dismissed, setDismissed] = useState(false);
  const [keywords] = useDebounce(text, DEBOUNCE_MS);
  const {lines, isLoading, isFetching} = useHeaderSearchLines(keywords);
  const boxRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const open = !dismissed && text.length >= MIN_SEARCH_KEYWORDS;
  // The clear button replaces the magnifier while the field holds text (DIS-2012).
  const hasText = text.length > 0;
  const clearProgress = useProgress(hasText, useReducedMotion());

  useEffect(() => {
    if (!open) {
      return;
    }
    onOpenChange?.(true);
    return () => onOpenChange?.(false);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !open) {
      return;
    }
    const onKeyDown = (event: {key?: string}) => {
      if (event.key === 'Escape') {
        setDismissed(true);
      }
    };
    // The panel is a child of the field box, so one `contains` covers both.
    // Closing on a backdrop pointerdown would unmount it and hand the click to the page below.
    const onPointerDown = (event: {target?: unknown}) => {
      const inside = [boxRef.current, backdropRef?.current].some(node =>
        containsTarget(node, event.target),
      );
      if (!inside) {
        setDismissed(true);
      }
    };
    // Read at event time: the header renders the backdrop after this effect runs. Capture
    // phase: the backdrop is a react-native-web Pressable, which stops the click's propagation
    // (measured in Chrome: a bubble listener never fired).
    const onClick = (event: {target?: unknown}) => {
      if (containsTarget(backdropRef?.current, event.target)) {
        setDismissed(true);
      }
    };
    const doc = document;
    doc.addEventListener('keydown', onKeyDown);
    doc.addEventListener('pointerdown', onPointerDown);
    doc.addEventListener('click', onClick, true);
    return () => {
      doc.removeEventListener('keydown', onKeyDown);
      doc.removeEventListener('pointerdown', onPointerDown);
      doc.removeEventListener('click', onClick, true);
    };
  }, [open, backdropRef]);

  // The header stays mounted under the next screen, so it is emptied on the way out.
  const reset = () => {
    setText('');
    setDismissed(false);
  };
  const submit = () => {
    const submitted = text;
    reset();
    onSubmit(submitted);
  };
  const pressResult = (line: OrderLine) => {
    reset();
    onResultPress(line);
  };

  // As SearchScreen: loading until the debounced keywords query; the empty copy only once the
  // query has settled with no lines.
  const queried = keywords.length >= MIN_SEARCH_KEYWORDS;
  const loading = !queried || isLoading;
  const empty = queried && !isFetching && !isLoading && lines.length === 0;

  return (
    <View
      ref={boxRef}
      testID={testID}
      style={[style, focused && Platform.OS === 'web' ? styles.fieldFocus : null]}>
      <TextInput
        ref={inputRef}
        testID={`${testID}-input`}
        value={text}
        onChangeText={next => {
          setText(filterSearchText(next));
          setDismissed(false);
        }}
        onSubmitEditing={submit}
        // RNW's TextInput stops keydown propagation, so the document listener never sees an
        // Escape typed in the field; the listener covers focus on the panel's cards.
        onKeyPress={event => {
          if (event.nativeEvent.key === 'Escape') {
            setDismissed(true);
            onEscape?.();
          }
        }}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
          onFocus?.();
        }}
        onBlur={() => setFocused(false)}
        // Figma: on opening, the default text goes and the user fills in a blank field.
        placeholder={focused ? '' : HEADER_SEARCH_PLACEHOLDER}
        placeholderTextColor={universal.greyDark}
        maxLength={MAX_LENGTH}
        autoCorrect={false}
        spellCheck={false}
        returnKeyType="search"
        accessibilityLabel="Search"
        style={[styles.input, Platform.OS === 'web' ? WEB_INPUT_NO_OUTLINE : null, textStyle]}
      />
      {/* One fixed slot holds both glyphs overlaid, so the swap never moves the input text.
          Only opacity and scale animate (motion.ts). Clearing drops the text under
          MIN_SEARCH_KEYWORDS, so the panel and the header's backdrop close. The button is
          inside boxRef: its pointerdown is not outside. */}
      <View testID={`${testID}-trailing`} style={styles.trailing}>
        <Animated.View
          testID={`${testID}-search-glyph`}
          style={[
            styles.glyph,
            {
              opacity: clearProgress.interpolate({inputRange: [0, 1], outputRange: [1, 0]}),
              transform: [
                {
                  scale: clearProgress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, GLYPH_HIDDEN_SCALE],
                  }),
                },
              ],
            },
          ]}
          {...hiddenWhen(hasText)}>
          <SearchIconImg
            viewBox={`0 0 ${SEARCH_ICON_SIZE} ${SEARCH_ICON_SIZE}`}
            width={SEARCH_ICON_SIZE}
            height={SEARCH_ICON_SIZE}
            color={universal.black}
            aria-hidden={true}
          />
        </Animated.View>
        <Animated.View
          testID={`${testID}-clear-glyph`}
          style={[
            styles.glyph,
            {
              opacity: clearProgress,
              transform: [
                {
                  scale: clearProgress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [GLYPH_HIDDEN_SCALE, 1],
                  }),
                },
              ],
            },
          ]}
          {...hiddenWhen(!hasText)}>
          <Pressable
            testID={`${testID}-clear`}
            disabled={!hasText}
            focusable={hasText}
            onPress={() => {
              reset();
              inputRef.current?.focus();
            }}
            style={styles.clear}
            accessibilityRole={hasText ? 'button' : undefined}
            accessibilityLabel={hasText ? 'Clear search' : undefined}>
            <SearchClearImg
              viewBox={`0 0 ${CLEAR_ICON_SIZE} ${CLEAR_ICON_SIZE}`}
              width={CLEAR_ICON_SIZE}
              height={CLEAR_ICON_SIZE}
              aria-hidden={true}
            />
          </Pressable>
        </Animated.View>
      </View>
      {open ? (
        <View
          testID={`${testID}-panel`}
          style={[styles.panel, {maxHeight: panelMaxHeight}]}
          accessibilityLabel="Search suggestions">
          <View style={styles.headerRow}>
            <Text style={styles.label}>Suggested</Text>
            <Pressable
              onPress={submit}
              style={styles.viewAll}
              accessibilityRole="button"
              accessibilityLabel="View All Results">
              <Text style={[styles.label, styles.accent]}>View All Results</Text>
              <ChevronRightImg
                viewBox={`0 0 ${CHEVRON_SIZE} ${CHEVRON_SIZE}`}
                width={CHEVRON_SIZE}
                height={CHEVRON_SIZE}
                color={headerChrome.accent}
                aria-hidden={true}
              />
            </Pressable>
          </View>
          {loading ? (
            <ScrollView testID={`${testID}-loading`} contentContainerStyle={styles.rows}>
              {Array.from({length: HEADER_SEARCH_LIMIT}, (_, i) => (
                <SearchItemSkeleton key={i} />
              ))}
            </ScrollView>
          ) : empty ? (
            <Text style={styles.empty}>NO PRODUCTS FOUND</Text>
          ) : (
            <ScrollView contentContainerStyle={styles.rows}>
              {lines.map(line => (
                <ProductTileHorizontal
                  key={line.id}
                  item={line}
                  pressCallback={pressResult}
                  testContext="header-search"
                />
              ))}
            </ScrollView>
          )}
        </View>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  // minWidth 0: the input shrinks with the field box instead of its intrinsic web size.
  input: {flex: 1, minWidth: 0, alignSelf: 'stretch', color: universal.black},
  // Replaces the input's browser outline. Drawn outside the pill, space.s2 off it, on the
  // header ground, so the colour is onGround (visible there on every brand). The offset
  // keeps a white ring (BM, HB) from reading as a thicker white pill.
  fieldFocus: {
    outlineStyle: 'solid',
    outlineWidth: control.borderWidth,
    outlineOffset: space.s2,
    outlineColor: headerChrome.onGround,
  },
  // Figma 196:25412: hangs under the field at the field's width (mobile 475:23215: the field is
  // the full width less the gutter). Deviation: border on every side plus a shadow, see
  // PANEL_SHADOW.
  panel: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    paddingTop: space.s8,
    paddingBottom: space.s16,
    paddingHorizontal: space.s12,
    gap: space.s6,
    backgroundColor: universal.white,
    borderWidth: control.borderWidth,
    borderColor: universal.greyMedium,
    borderBottomLeftRadius: radius.control,
    borderBottomRightRadius: radius.control,
    boxShadow: PANEL_SHADOW,
  },
  headerRow: {
    height: HEADER_ROW_HEIGHT,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // The magnifier's frame (Figma 196:26065), as before the swap: the empty field keeps its
  // width. The clear hit frame overhangs it; overflow 'visible' on the slot and the glyph
  // stops RN Android from clipping the overhang. The field box sets no overflow.
  trailing: {width: SEARCH_ICON_SIZE, height: SEARCH_ICON_SIZE, overflow: 'visible'},
  glyph: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  // Centred on the magnifier. Not hitSlop: react-native-web ignores it.
  clear: {
    position: 'absolute',
    top: -CLEAR_OVERHANG,
    left: -CLEAR_OVERHANG,
    width: CLEAR_HIT_SIZE,
    height: CLEAR_HIT_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewAll: {flexDirection: 'row', alignItems: 'center', gap: space.s4},
  label: {...chromeType.label12Medium, color: universal.black},
  accent: {color: headerChrome.accent},
  rows: {gap: space.s6},
  empty: {...chromeType.label12Medium, color: universal.black, textAlign: 'center'},
});
